// Der laufende Ticket-Timer für die ganze App — Grundlage des Chips in der Kopfleiste.
//
// Warum ein eigener Bestand neben ZeitContext: der ZeitContext beantwortet „wann habe ich
// gearbeitet" (Stempeluhr), dieser hier „woran arbeite ich gerade". Beide Werte stehen
// nebeneinander und dürfen nie addiert werden (siehe lib/zeiterfassung/ticketzeit.ts).
//
// Aktualisiert wird zweifach: die Ticket-Ansicht meldet Start und Stopp sofort über
// `neuLaden()`, und ein Minutentakt holt nach, was auf einem anderen Gerät passiert ist.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { laufenderTicketTimerMitTicket, timerStoppen } from '../lib/zeiterfassung/api';
import type { TicketZeit } from '../lib/zeiterfassung/ticketzeit';

export interface LaufenderTimer {
  zeit: TicketZeit;
  ticketNummer: string | null;
  ticketTitel: string | null;
}

interface TicketTimerContextValue {
  /** Null, solange nichts läuft — der Chip blendet dann komplett aus. */
  timer: LaufenderTimer | null;
  /** Sekundengenaue Uhr; tickt nur, solange ein Timer läuft. */
  jetzt: Date;
  neuLaden: () => Promise<void>;
  /** Laufenden Timer beenden (Pille). Liefert false bei Fehler. */
  stoppen: () => Promise<boolean>;
}

/** Tätigkeit, wenn der Timer ohne eigene Beschreibung aus der Pille gestoppt wird. */
export const PILLEN_TAETIGKEIT = 'Arbeitszeit (Timer)';

const LEER: TicketTimerContextValue = { timer: null, jetzt: new Date(), neuLaden: async () => {}, stoppen: async () => false };

const TicketTimerContext = createContext<TicketTimerContextValue | null>(null);

export const useTicketTimer = (): TicketTimerContextValue => useContext(TicketTimerContext) ?? LEER;

/** Wie oft ohne eigenes Zutun nachgesehen wird (anderes Gerät, anderer Tab). */
const ABGLEICH_MS = 60_000;

export const TicketTimerProvider: React.FC<{ userId: string | null; children: React.ReactNode }> = ({ userId, children }) => {
  const [timer, setTimer] = useState<LaufenderTimer | null>(null);
  const [jetzt, setJetzt] = useState(() => new Date());

  const neuLaden = useCallback(async () => {
    if (!userId) { setTimer(null); return; }
    setTimer(await laufenderTicketTimerMitTicket(userId));
  }, [userId]);

  useEffect(() => { void neuLaden(); }, [neuLaden]);

  // Sekundentakt nur bei laufendem Timer — sonst rendert die halbe App im Leerlauf mit.
  useEffect(() => {
    if (!timer) return;
    const t = setInterval(() => setJetzt(new Date()), 1000);
    return () => clearInterval(t);
  }, [timer]);

  useEffect(() => {
    if (!userId) return;
    const t = setInterval(() => { void neuLaden(); }, ABGLEICH_MS);
    return () => clearInterval(t);
  }, [userId, neuLaden]);

  const stoppen = useCallback(async () => {
    if (!timer) return false;
    const ok = await timerStoppen(timer.zeit.id, PILLEN_TAETIGKEIT);
    await neuLaden();
    return ok;
  }, [timer, neuLaden]);

  const wert = useMemo<TicketTimerContextValue>(() => ({ timer, jetzt, neuLaden, stoppen }), [timer, jetzt, neuLaden, stoppen]);

  return <TicketTimerContext.Provider value={wert}>{children}</TicketTimerContext.Provider>;
};
